import { type FC, useState, useMemo, useCallback } from 'react';
import type { Node as ReactFlowNode, Edge as ReactFlowEdge } from 'reactflow';
import type { NodeData } from '@/types/node.types';
import type { LinkData } from '@/types/link.types';
import { LinkType } from '@/types/link.types';
import { NODE_TYPE_COLORS, NODE_STATUS_LABELS } from '@/types/node.types';
import { useReactFlow } from 'reactflow';
import { exportTreeToXLSX } from '@/services/exportXlsx';
import { useCanvasStore } from '@/stores/canvas.store';

interface TreeViewProps {
  nodes: ReactFlowNode<NodeData>[];
  edges: ReactFlowEdge<LinkData>[];
  onSelectNode?: (nodeId: string) => void;
}

const LINK_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'Tous les liens' },
  { value: LinkType.Hierarchy, label: 'Hi\u00e9rarchie' },
  { value: LinkType.Dependency, label: 'D\u00e9pendance' },
  { value: LinkType.Contribution, label: 'Contribution' },
  { value: LinkType.Contradiction, label: 'Contradiction' },
  { value: LinkType.Complementarity, label: 'Compl\u00e9mentarit\u00e9' },
  { value: LinkType.FreeForm, label: 'Libre' },
];

const NODE_ICONS: Record<string, string> = {
  Vision: '\u2605',
  Objective: '\u25CE',
  Activity: '\u26A1',
  Risk: '\u26A0',
  Opportunity: '\u2728',
  Resource: '\u2699',
  KPI: '\u{1F4CA}',
  Stakeholder: '\u{1F465}',
};

const statusColors: Record<string, string> = {
  Active: '#22C55E',
  Paused: '#F59E0B',
  Archived: '#6B7280',
  ToReview: '#8B5CF6',
};

interface TreeItemData {
  node: ReactFlowNode<NodeData>;
  children: TreeItemData[];
}

const TreeItem: FC<{
  item: TreeItemData;
  depth: number;
  onSelect: (nodeId: string) => void;
}> = ({ item, depth, onSelect }) => {
  const [expanded, setExpanded] = useState(true);
  const { data } = item.node;
  const color = NODE_TYPE_COLORS[data.type] || '#6B7280';
  const hasChildren = item.children.length > 0;

  return (
    <div>
      <div
        onClick={() => onSelect(item.node.id)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-xs)',
          padding: 'var(--spacing-xs) var(--spacing-sm)',
          paddingLeft: `calc(var(--spacing-sm) + ${depth * 20}px)`,
          cursor: 'pointer',
          borderRadius: 'var(--radius-sm)',
          transition: 'var(--transition-fast)',
          backgroundColor: 'transparent',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'var(--color-background)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        {/* Expand/collapse */}
        <span
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(!expanded);
          }}
          style={{
            width: 16,
            textAlign: 'center',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-muted)',
            cursor: hasChildren ? 'pointer' : 'default',
            userSelect: 'none',
          }}
        >
          {hasChildren ? (expanded ? '\u25BC' : '\u25B6') : ''}
        </span>

        {/* Node type icon */}
        <span style={{ fontSize: 'var(--font-size-sm)', color }}>
          {NODE_ICONS[data.type] || '\u25CF'}
        </span>

        {/* Title */}
        <span
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text)',
            fontWeight: 500,
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {data.title}
        </span>

        {/* Priority badge */}
        <span
          style={{
            fontSize: 'var(--font-size-xs)',
            fontWeight: 700,
            color: '#fff',
            backgroundColor: color,
            borderRadius: 'var(--radius-sm)',
            padding: '0 5px',
            lineHeight: '18px',
          }}
        >
          P{data.priority}
        </span>

        {/* Status dot */}
        <span
          title={NODE_STATUS_LABELS[data.status]}
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: statusColors[data.status] || '#6B7280',
            flexShrink: 0,
          }}
        />
      </div>

      {/* Children */}
      {expanded && hasChildren && (
        <div>
          {item.children.map((child) => (
            <TreeItem
              key={child.node.id}
              item={child}
              depth={depth + 1}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const TreeView: FC<TreeViewProps> = ({ nodes, edges, onSelectNode }) => {
  const [linkFilter, setLinkFilter] = useState('all');
  const reactFlowInstance = useReactFlow();
  const canvasName = useCanvasStore((s) => s.canvas?.name ?? 'canvas');

  const filteredEdges = useMemo(() => {
    if (linkFilter === 'all') return edges;
    return edges.filter((e) => e.data?.linkType === linkFilter);
  }, [edges, linkFilter]);

  const tree = useMemo(() => {
    // Build adjacency: source -> targets
    const childrenMap = new Map<string, string[]>();
    const hasParent = new Set<string>();

    for (const edge of filteredEdges) {
      const list = childrenMap.get(edge.source) || [];
      list.push(edge.target);
      childrenMap.set(edge.source, list);
      hasParent.add(edge.target);
    }

    // Root nodes = nodes with no incoming edges in the filtered set
    const roots = nodes.filter((n) => !hasParent.has(n.id) && n.type !== 'group');

    const visited = new Set<string>();

    function buildTree(nodeId: string): TreeItemData | null {
      const node = nodes.find((n) => n.id === nodeId);
      if (!node || visited.has(nodeId)) return null;
      visited.add(nodeId);

      const childIds = childrenMap.get(nodeId) || [];
      const children: TreeItemData[] = [];
      for (const childId of childIds) {
        const child = buildTree(childId);
        if (child) children.push(child);
      }

      return { node, children };
    }

    const items: TreeItemData[] = [];
    for (const root of roots) {
      const item = buildTree(root.id);
      if (item) items.push(item);
    }

    // Add orphan nodes not visited
    for (const n of nodes) {
      if (!visited.has(n.id) && n.type !== 'group') {
        items.push({ node: n, children: [] });
      }
    }

    return items;
  }, [nodes, filteredEdges]);

  const handleSelect = useCallback(
    (nodeId: string) => {
      const node = nodes.find((n) => n.id === nodeId);
      if (!node) return;
      if (onSelectNode) onSelectNode(nodeId);
      reactFlowInstance.setCenter(node.position.x + 100, node.position.y + 50, {
        zoom: 1.2,
        duration: 400,
      });
    },
    [nodes, reactFlowInstance, onSelectNode],
  );

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        backgroundColor: 'var(--color-surface)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: 'var(--spacing-md)',
          borderBottom: '1px solid var(--color-border)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-md)',
        }}
      >
        <span
          style={{
            fontSize: 'var(--font-size-md)',
            fontWeight: 600,
            color: 'var(--color-text)',
          }}
        >
          Vue arborescente
        </span>

        <select
          value={linkFilter}
          onChange={(e) => setLinkFilter(e.target.value)}
          style={{
            padding: 'var(--spacing-xs) var(--spacing-sm)',
            backgroundColor: 'var(--color-background)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--color-text)',
            fontSize: 'var(--font-size-xs)',
            cursor: 'pointer',
            outline: 'none',
          }}
        >
          {LINK_FILTER_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        <div style={{ marginLeft: 'auto' }}>
          <button
            onClick={() => exportTreeToXLSX(canvasName, tree)}
            title="Exporter l'arbre filtré en XLSX"
            style={{
              padding: 'var(--spacing-xs) var(--spacing-sm)',
              backgroundColor: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--color-text)',
              cursor: 'pointer',
              fontSize: 'var(--font-size-xs)',
            }}
          >
            Exporter XLSX
          </button>
        </div>
      </div>

      {/* Tree content */}
      <div
        style={{
          flex: 1,
          overflow: 'auto',
          padding: 'var(--spacing-sm)',
        }}
      >
        {tree.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: 'var(--spacing-xl)',
              color: 'var(--color-text-muted)',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            Aucun noeud trouv\u00e9
          </div>
        ) : (
          tree.map((item) => (
            <TreeItem
              key={item.node.id}
              item={item}
              depth={0}
              onSelect={handleSelect}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default TreeView;
